import React, { useState, useMemo } from 'react';
import type { BeneficiaryDetailsData, Beneficiary } from '../../../types/customer-portal';
import BeneficiaryModal from './BeneficiaryModal';
import { formatFullName } from '../../../utils/nameFormatter';
import './BeneficiaryDetailsStep.css';

interface BeneficiaryDetailsStepProps {
  data?: BeneficiaryDetailsData;
  onNext: (data: BeneficiaryDetailsData) => void;
  onBack: () => void;
}

const BeneficiaryDetailsStep: React.FC<BeneficiaryDetailsStepProps> = ({
  data,
  onNext,
  onBack
}) => {
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>(
    data?.beneficiaries || []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<Beneficiary | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const maxBeneficiaries = 3;
  const canAddMore = beneficiaries.length < maxBeneficiaries;

  // Form validation - at least one beneficiary is required, and every beneficiary must have a Title
  const isFormValid = useMemo(() => {
    return (
      beneficiaries.length > 0 &&
      beneficiaries.every((b) => !!b.title?.trim())
    );
  }, [beneficiaries]);

  const handleAddBeneficiary = () => {
    if (canAddMore) {
      setEditingBeneficiary(null);
      setIsEditMode(false);
      setIsModalOpen(true);
    }
  };

  const handleEditBeneficiary = (beneficiary: Beneficiary) => {
    setEditingBeneficiary(beneficiary);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleSaveBeneficiary = (beneficiary: Beneficiary) => {
    if (isEditMode && editingBeneficiary) {
      // Update existing beneficiary
      setBeneficiaries(prev => 
        prev.map(b => b.id === beneficiary.id ? beneficiary : b)
      );
    } else {
      // Add new beneficiary
      setBeneficiaries(prev => [...prev, beneficiary]);
    }
  };

  const handleDeleteBeneficiary = (beneficiaryId: string) => {
    setBeneficiaries(prev => prev.filter(b => b.id !== beneficiaryId));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBeneficiary(null);
    setIsEditMode(false);
  };

  const handleNext = () => {
    const beneficiaryData: BeneficiaryDetailsData = {
      beneficiaries: beneficiaries
    };
    onNext(beneficiaryData);
  };


  return (
    <>
      <div className="beneficiary-details-step">
        <div className="beneficiary-information-step-header">
          <div className="step-header-content">
            <h3 className="step-title">Beneficiary Information</h3>
            <p className="step-description">
              You may designate up to 3 beneficiaries who will be eligible to receive benefits from this travel insurance in case of a claim.
            </p>
          </div>
          {canAddMore && (
            <button
              className="add-beneficiary-btn"
              onClick={handleAddBeneficiary}
              aria-label="Add beneficiary"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          )}
        </div>

        <div className="step-content">
          {beneficiaries.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-text">
                No beneficiaries added yet. Click the + button to add a beneficiary.
              </p>
            </div>
          ) : (
            <div className="beneficiaries-list">
              {beneficiaries.map((beneficiary) => (
                <div key={beneficiary.id} className="beneficiary-card">
                  <div className="beneficiary-info">
                    <h4 className="beneficiary-name">
                      {formatFullName(beneficiary)}
                    </h4>
                    <p className="beneficiary-relationship">{beneficiary.relationship}</p>
                    {!beneficiary.title?.trim() && (
                      <p className="beneficiary-missing-title">
                        Title is required. Click edit to update.
                      </p>
                    )}
                  </div>
                  <button
                    className="edit-beneficiary-btn"
                    onClick={() => handleEditBeneficiary(beneficiary)}
                    aria-label={`Edit ${beneficiary.firstName} ${beneficiary.lastName}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {beneficiaries.length >= maxBeneficiaries && (
            <div className="max-beneficiaries-notice">
              <p>Maximum of {maxBeneficiaries} beneficiaries reached.</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className={`btn-next ${!isFormValid ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={!isFormValid}
        >
          Next
        </button>
      </div>

      {/* Beneficiary Modal */}
      <BeneficiaryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveBeneficiary}
        onDelete={handleDeleteBeneficiary}
        beneficiary={editingBeneficiary}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default BeneficiaryDetailsStep;